/**
 * Mount Rigi recipe page
 *
 *   - OZ / ML switch. Both amounts are authored on the ingredient, so this only
 *     swaps which one is visible. The choice is remembered, so a visitor who
 *     works in millilitres gets millilitres on every recipe.
 *   - Party drink calculator. Scales the recipe's own amounts and turns them
 *     into shopping units using rules from the section settings. No data of its
 *     own, no network request.
 *   - Shopping list to the clipboard, share, print.
 *
 * Without JavaScript the page stays complete: ounces are shown, the recipe
 * reads normally, only the calculator is absent.
 */
(() => {
  'use strict';

  const UNIT_KEY = 'mr-recipe-units';
  const OZ_PER_ML = 0.033814;

  /* Garnishes worth counting, and the fruit they come from. A line is only
     turned into a number when one of these words is in it - "Salt rim and lime
     wheel" is a lime, not a salt, and "Pinch of sea salt" is nothing you buy
     by the piece. */
  const FRUITS = ['grapefruit', 'lime', 'lemon', 'orange', 'blood orange'];
  const SEASONINGS = /(salt|pepper|sugar|bitters|pinch|dash|rim)/;

  const readUnit = () => {
    try {
      return window.localStorage.getItem(UNIT_KEY) === 'ml' ? 'ml' : 'oz';
    } catch (_) {
      return 'oz';
    }
  };

  const saveUnit = (unit) => {
    try {
      window.localStorage.setItem(UNIT_KEY, unit);
    } catch (_) {
      // Private mode: the switch still works for this visit.
    }
  };

  const words = (value) =>
    (value || '')
      .toLowerCase()
      .split(',')
      .map((word) => word.trim())
      .filter(Boolean);

  const matches = (name, list) => list.some((word) => name.includes(word));

  /* 1.5 -> "1.5", 2.0 -> "2", 0.333 -> "0.3" */
  const tidy = (value, decimals = 1) => String(Number(value.toFixed(decimals)));

  const plural = (count, singular, pluralForm) => `${count} ${count === 1 ? singular : pluralForm}`;

  class RecipePage {
    constructor(root) {
      this.root = root;
      this.unit = readUnit();

      this.bindUnits();
      this.bindActions();
      this.bindCalculator();
      this.applyUnit();
      this.calculate();
    }

    /* ---------- units ---------- */

    bindUnits() {
      this.unitButtons = Array.from(this.root.querySelectorAll('[data-mr-unit-switch]'));
      this.unitButtons.forEach((button) => {
        button.addEventListener('click', () => {
          this.unit = button.dataset.mrUnitSwitch === 'ml' ? 'ml' : 'oz';
          saveUnit(this.unit);
          this.applyUnit();
          this.calculate();
        });
      });
    }

    applyUnit() {
      this.root.querySelectorAll('[data-mr-unit]').forEach((element) => {
        element.hidden = element.dataset.mrUnit !== this.unit;
      });
      this.unitButtons.forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.mrUnitSwitch === this.unit));
      });
    }

    /* ---------- print, share, copy ---------- */

    bindActions() {
      this.status = this.root.querySelector('[data-mr-recipe-status]');

      this.root.querySelector('[data-mr-recipe-print]')?.addEventListener('click', () => window.print());

      this.root.querySelector('[data-mr-recipe-share]')?.addEventListener('click', async (event) => {
        const title = event.currentTarget.dataset.shareTitle || document.title;
        const url = window.location.href;

        // The system share sheet where it exists - that is where the messaging
        // apps people actually use live. Clipboard everywhere else.
        if (navigator.share) {
          try {
            await navigator.share({ title, url });
            return;
          } catch (_) {
            return; // Cancelled by the visitor: say nothing.
          }
        }
        this.copy(url, 'Link copied.');
      });

      this.root.querySelector('[data-mr-calc-copy]')?.addEventListener('click', () => {
        this.copy(this.shoppingList(), 'Shopping list copied.');
      });
    }

    async copy(text, message) {
      try {
        await navigator.clipboard.writeText(text);
        this.say(message);
      } catch (_) {
        this.say('Could not copy - please select the text instead.');
      }
    }

    say(message) {
      if (!this.status) return;
      this.status.textContent = message;
      window.clearTimeout(this.statusTimer);
      this.statusTimer = window.setTimeout(() => {
        this.status.textContent = '';
      }, 4000);
    }

    /* ---------- calculator ---------- */

    bindCalculator() {
      const data = this.root.querySelector('[data-mr-recipe-amounts]');
      if (!data) return;

      try {
        this.amounts = JSON.parse(data.textContent);
      } catch (_) {
        this.amounts = [];
      }

      const set = this.root.dataset;
      this.rules = {
        bottleMl: Number(set.bottleMl) || 750,
        otherBottleMl: Number(set.otherBottleMl) || 700,
        alcohol: words(set.alcoholWords),
        mixers: words(set.mixerWords),
        icePerDrink: Number(set.icePerDrink) || 0.3,
        iceBag: Number(set.iceBag) || 7,
        citrusPerFruit: Number(set.citrusPerFruit) || 8,
      };

      this.guests = this.root.querySelector('[data-mr-calc-guests]');
      this.hours = this.root.querySelector('[data-mr-calc-hours]');
      this.guestsValue = this.root.querySelector('[data-mr-calc-guests-value]');
      this.hoursValue = this.root.querySelector('[data-mr-calc-hours-value]');
      this.drinksOut = this.root.querySelector('[data-mr-calc-drinks]');
      this.listOut = this.root.querySelector('[data-mr-calc-list]');
      this.quantity = this.root.querySelector('[data-mr-calc-quantity]');

      this.root.querySelectorAll('[data-mr-calc] input').forEach((input) => {
        input.addEventListener('input', () => this.calculate());
        input.addEventListener('change', () => this.calculate());
      });
    }

    factor(group) {
      const checked = this.root.querySelector(`[data-mr-calc-group="${group}"] input:checked`);
      return checked ? Number(checked.value) || 1 : 1;
    }

    /* The planning rule every bar and caterer uses: two drinks in the first
       hour, one in every hour after that. The two questions adjust it - an
       evening without food runs faster, a full meal slows it down. */
    drinkCount() {
      const guests = Number(this.guests?.value) || 0;
      const hours = Number(this.hours?.value) || 1;
      const base = guests * (hours + 1);
      return Math.ceil(base * this.factor('vibe') * this.factor('food'));
    }

    calculate() {
      if (!this.amounts) return;

      const drinks = this.drinkCount();
      if (this.guestsValue) this.guestsValue.textContent = this.guests.value;
      if (this.hoursValue) this.hoursValue.textContent = this.hours.value;

      this.lines = this.amounts.map((item) => this.line(item, drinks)).filter(Boolean);

      if (this.drinksOut) this.drinksOut.textContent = plural(drinks, 'drink', 'drinks');
      if (this.listOut) this.listOut.textContent = this.lines.join(', ');

      // The bottles the host needs are put straight into the quantity field,
      // so "add to cart" already carries the right number.
      if (this.quantity && this.heroBottles > 0) {
        this.quantity.value = String(this.heroBottles);
        this.quantity.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    line(item, drinks) {
      const name = (item.name || '').toLowerCase();
      const ml = Number(item.ml) || 0;
      const oz = Number(item.oz) || 0;

      /* Amounts present: scale, then convert into something you can buy. */
      if (ml > 0 || oz > 0) {
        const totalMl = (ml || oz / OZ_PER_ML) * drinks;

        if (item.hero) {
          this.heroBottles = Math.ceil(totalMl / this.rules.bottleMl);
          return `${plural(this.heroBottles, 'bottle', 'bottles')} ${item.name}`;
        }

        if (matches(name, this.rules.alcohol)) {
          return `${plural(Math.ceil(totalMl / this.rules.otherBottleMl), 'bottle', 'bottles')} ${item.name}`;
        }

        // Mixers and juices are bought by volume, in the unit on screen.
        return this.unit === 'ml'
          ? `${tidy(totalMl / 1000)} L ${item.name}`
          : `${Math.ceil(totalMl * OZ_PER_ML)} oz ${item.name}`;
      }

      /* No amount: ice, garnishes, seasoning. */
      if (name.includes('ice')) {
        const pounds = drinks * this.rules.icePerDrink;
        const bags = Math.ceil(pounds / this.rules.iceBag);
        return this.unit === 'ml'
          ? `${Math.ceil(pounds * 0.4536)} kg ice (${plural(bags, 'bag', 'bags')})`
          : `${Math.ceil(pounds)} lb ice (${plural(bags, 'bag', 'bags')})`;
      }

      /* A fruit is counted only when the line actually names one. That keeps
         "Salt rim and lime wheel" a lime, and keeps salt out of the list
         entirely: nobody shops for "10 salts", and a rim needs a pinch. */
      const fruit = FRUITS.find((entry) => name.includes(entry));
      if (fruit) {
        const fruits = Math.ceil(drinks / this.rules.citrusPerFruit);
        return plural(fruits, fruit, `${fruit}s`);
      }

      // Salt, sugar, bitters, a pinch of this or that: real, but not a number.
      if (SEASONINGS.test(name)) return null;

      return null;
    }

    shoppingList() {
      const title = document.title;
      const drinks = this.drinksOut?.textContent || '';
      const lines = (this.lines || []).map((line) => `- ${line}`).join('\n');
      return `${title}\n${drinks}\n\n${lines}\n\n${window.location.href}`;
    }
  }

  const initialise = (scope = document) => {
    scope.querySelectorAll('[data-mr-recipe]').forEach((root) => {
      if (root.dataset.mrRecipeReady === 'true') return;
      root.dataset.mrRecipeReady = 'true';
      new RecipePage(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise(), { once: true });
  } else {
    initialise();
  }

  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
